import { t } from "i18next";
import { Debug } from "../../../utils/debug";

import { StatusTag } from "../../../components/deprecated/StatusTag/StatusTag";
import { FirebaseKeyData } from "../../../to-be-removed/LocalData/Company/FirebaseKeyData";
import { FirebaseLockData } from "../../../to-be-removed/LocalData/Company/FirebaseLockData";
import { FirebaseLockholderRoleData } from "../../../to-be-removed/LocalData/Company/FirebaseLockholderRoleData";
import { LockHolderAccessTokenData } from "../../../to-be-removed/LocalData/Company/LockHolderAccessTokenData";
import { LockHolderData } from "../../../to-be-removed/LocalData/Company/LockHolderData";
import CollectionLoader, { CollectionLoadedListener } from "../../../to-be-removed/LocalData/General/CollectionLoader";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { UnlocHelper } from "../../../to-be-removed/LocalData/Property/Integrations/Unloc";
import {
  UnlocKey,
  UnlocLock,
  UnlocRole,
  UnlocToUser,
  UnlocVendorConnection,
} from "../../../to-be-removed/LocalData/Property/Integrations/Unloc/UnlocTypes";
import { MemberData } from "../../../to-be-removed/LocalData/Property/MemberData";
import { PropertyData } from "../../../to-be-removed/LocalData/Property/PropertyData";
import { UserData } from "../../../to-be-removed/LocalData/User/UserData";

export class LocksLoader {
  static allCollections: CollectionLoader<LockHolderData>[] = [];
  static allListeners: CollectionLoadedListener<LockHolderData>[] = [];
  static allFbLocksListeners: CollectionLoadedListener<FirebaseLockData>[] = [];
  static allFbKeysListeners: CollectionLoadedListener<FirebaseKeyData>[] = [];
  static allFbRolesListeners: CollectionLoadedListener<FirebaseLockholderRoleData>[] = [];
  static allAccessTokensListeners: CollectionLoadedListener<LockHolderAccessTokenData>[] = [];
  static includeRoles = true;
  static includeKeyEvents = true;

  static listenForLockholderUpdates(completion: () => void) {
    this.allCollections = LocalData.currentUser.companies.map((c) => c.lockHolders);
    if (this.allCollections.length === 0) completion();

    this.allListeners = this.allCollections.map((collection) =>
      collection.setupRealtimeListener((docs) => {
        const checkForFinish = () => {
          for (const collection of this.allCollections) {
            if (!collection.loaded) return;

            for (const lockholder of collection.documents) {
              if (lockholder.hasError) continue;
              if (
                !lockholder.fbLocks.loaded ||
                !lockholder.accessTokens.loaded ||
                !lockholder.scopedAuth ||
                !lockholder.keys.loaded ||
                !lockholder.locks.loaded ||
                (!lockholder.roles.loaded && this.includeRoles)
              )
                return;
            }
          }

          completion();
        };
        if (docs.length === 0) checkForFinish();
        for (const lh of docs) {
          if (lh.accessTokens.realtimeListeners.length === 0) {
            this.allAccessTokensListeners.push(
              lh.accessTokens.setupRealtimeListener(() => {
                loadLockHolderData();
              })
            );
          }
          if (lh.fbLocks.realtimeListeners.length === 0) {
            this.allFbLocksListeners.push(
              lh.fbLocks.setupRealtimeListener(() => {
                checkForFinish();
              })
            );
          }
          if (lh.fbKeys.realtimeListeners.length === 0) {
            this.allFbKeysListeners.push(
              lh.fbKeys.setupRealtimeListener(() => {
                checkForFinish();
              })
            );
          }
          if (lh.fbRoles.realtimeListeners.length === 0) {
            this.allFbRolesListeners.push(
              lh.fbRoles.setupRealtimeListener(() => {
                checkForFinish();
              })
            );
          }

          const loadLockHolderData = () => {
            lh.getScopedAuthData(() => {
              checkForFinish();

              if (!lh.locks.loaded && !lh.locks.loading) {
                Debug.print("Loading locks for " + lh.id);
                lh.getLocks(() => {
                  Debug.print("Loaded locks for " + lh.id);
                  checkForFinish();
                }, false);
              }
              if (!lh.keys.loaded && !lh.keys.loading) {
                Debug.print("Loading keys for " + lh.id);
                lh.getKeys(() => {
                  Debug.print("Loaded keys for " + lh.id);
                  checkForFinish();
                });
              }
              if (!lh.roles.loaded && !lh.roles.loading && this.includeRoles) {
                Debug.print("Loading roles for " + lh.id);
                lh.getRoles(() => {
                  Debug.print("Loaded roles for " + lh.id);
                  checkForFinish();
                });
              }
            });
          };
        }
      })
    );
  }

  static removeRealtimeListeners() {
    for (const listener of this.allListeners) {
      listener.removeListener();
    }
  }

  // HELPER FUNCTIONS
  static InvalidStates = ["expired", "error", "revoked"];

  static getLockHolderForKey(key: UnlocKey): LockHolderData | undefined {
    for (const collection of this.allCollections) {
      for (const lockHolder of collection.documents) {
        if (lockHolder.keys.datas.some((old) => old.id === key.id)) {
          return lockHolder;
        }
      }
    }
    return undefined;
  }

  static getLockHolderForLock(lock: UnlocLock): LockHolderData | undefined {
    for (const collection of this.allCollections) {
      for (const lockHolder of collection.documents) {
        if (lockHolder.locks.datas.some((old) => old.id === lock.id)) {
          return lockHolder;
        }
      }
    }
    return undefined;
  }
  static getLockHolderForLockId(lockId: string): LockHolderData | undefined {
    for (const collection of this.allCollections) {
      for (const lockHolder of collection.documents) {
        if (lockHolder.locks.datas.some((old) => old.id === lockId)) {
          return lockHolder;
        }
      }
    }
    return undefined;
  }
  static getLockWithId(id: string): UnlocLock | undefined {
    for (const loader of this.allCollections) {
      for (const collection of loader.documents) {
        for (const lock of collection.locks.datas) {
          if (lock.id === id) return lock;
        }
      }
    }
    return undefined;
  }
  static getFirebaseLockForKey(key: UnlocKey): FirebaseLockData | undefined {
    return this.getLockHolderForKey(key)?.fbLocks.documents.find((fbLock) => fbLock.unlocLockId === key.lockId);
  }
  static getFirebaseLockForLock(lock: UnlocLock): FirebaseLockData | undefined {
    return this.getLockHolderForLock(lock)?.fbLocks.documents.find((fbLock) => fbLock.unlocLockId === lock.id);
  }

  static filterValidKeys(keys: UnlocKey[]): UnlocKey[] {
    return keys.filter((key) => !this.InvalidStates.includes(key.state));
  }

  static getValidKeysForLock(lockHolder: LockHolderData | undefined, lock: UnlocLock): UnlocKey[] {
    return (
      (lockHolder ?? this.getLockHolderForLock(lock))?.keys.datas.filter(
        (key) => key.lockId === lock.id && !this.InvalidStates.includes(key.state)
      ) ?? []
    );
  }

  static getValidKeysForPhone(lockHolder: LockHolderData, phone: string): UnlocKey[] {
    phone = UnlocHelper.convertPhoneToE164(lockHolder, phone);
    return (
      lockHolder.keys.datas.filter(
        (key) => UnlocHelper.convertPhoneToE164(lockHolder, key.toUser.id) === phone && !this.InvalidStates.includes(key.state)
      ) ?? []
    );
  }

  static getValidKeysForPhoneInLockholders(lockHolders: LockHolderData[], phone: string): UnlocKey[] {
    const keys: UnlocKey[] = [];
    for (const lockHolder of lockHolders) {
      keys.push(...this.getValidKeysForPhone(lockHolder, phone));
    }
    return keys;
  }

  static getInvalidKeys(lockHolder: LockHolderData, lock: UnlocLock): UnlocKey[] {
    return lockHolder.keys.datas
      .filter((key) => key.lockId === lock.id && this.InvalidStates.includes(key.state))
      .sort((a, b) => {
        return new Date(a.start ?? "") < new Date(b.start ?? "") ? -1 : 1;
      });
  }

  static getMemberForPhone(lockholder: LockHolderData, phone: string, property: PropertyData | undefined): MemberData | undefined {
    phone = UnlocHelper.convertPhoneToE164(lockholder, phone);
    for (const member of property?.members.documents ?? []) {
      if (UnlocHelper.convertPhoneToE164(lockholder, member.user.phone) === phone) {
        return member;
      }
    }
    return undefined;
  }
  static getRoleForPhone(phone: string, lock: UnlocLock, lockholder: LockHolderData): UnlocRole | undefined {
    phone = UnlocHelper.convertPhoneToE164(lockholder, phone);
    return lockholder.roles.datas.find((role) => UnlocHelper.convertPhoneToE164(lockholder, role.userId) === phone && role.lockId === lock.id);
  }

  static getStatusTagForKey(state: string | undefined | null): JSX.Element {
    let text = state ?? "";
    if (!state) {
      text = t("main.noAccess");
    } else if (t(`main.unlocState_${state}`, "null") !== "null") {
      text = t(`main.unlocState_${state}`);
    }
    const colors: { [key: string]: any } = {
      "": "orange",
      creating: "green",
      scheduled: "green",
      active: "green",
      expired: "red",
      error: "red",
      revoked: "red",
    };

    return <StatusTag color={colors[state ?? ""]} text={text} />;
  }

  static isConnectionValid(connection: UnlocVendorConnection): boolean {
    return connection.state === "connected" || connection.state === "connecting" || connection.state === "refreshing";
  }

  static getCustomNameForPhone(phone: string): string | undefined {
    for (const collection of LocksLoader.allCollections) {
      for (const lockholder of collection.documents) {
        const customName = lockholder.fbRoles.getData(phone)?.userName;
        if (customName && customName.length > 0) return customName;
      }
    }
    return undefined;
  }

  static getNameForToUser(toUser: UnlocToUser, user: UserData | undefined): string {
    const customName = this.getCustomNameForPhone(toUser.id);
    if (customName) return customName;
    if (toUser.name) return toUser.name;
    if (user?.name) return user.name;

    for (const collection of LocksLoader.allCollections) {
      for (const lockholder of collection.documents) {
        const name = LocksLoader.getNameForPhone(lockholder, toUser.id);
        if (name && name.length > 0) return name;
      }
    }
    return t("main.unspecified");
  }

  static getNameForPhone(lockholder: LockHolderData | undefined, phone: string): string {
    phone = UnlocHelper.convertPhoneToE164(lockholder, phone);
    let name =
      lockholder?.fbRoles.getData(phone)?.userName ??
      lockholder?.keys.datas.find((key) => UnlocHelper.convertPhoneToE164(lockholder, key.toUser.id) === phone)?.toUser.name ??
      lockholder?.roles.datas.find((role) => UnlocHelper.convertPhoneToE164(lockholder, role.userId) === phone)?.userName;
    if (name && name.length > 0) return name;

    for (const user of LocalData.users.documents) {
      if (user.phone && UnlocHelper.convertPhoneToE164(lockholder, user.phone) === phone) {
        if (user.name) return user.name;
      }
    }

    return "";
  }

  static getUserForPhone(lockholder: LockHolderData | undefined, phone: string): UserData | undefined {
    for (const user of LocalData.users.documents) {
      if (user.phone && UnlocHelper.convertPhoneToE164(lockholder, user.phone) === phone) {
        if (user.name) return user;
      }
    }
    return undefined;
  }

  static getStatusTagColorForVendorConnection(connection: UnlocVendorConnection): "orange" | "red" | "green" | "blue" {
    if (connection.state === "connected") {
      return "green";
    } else if (connection.state === "disconnected" || connection.state === "error") {
      return "red";
    } else {
      return "orange";
    }
  }
}

